import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  assignDisplayNumbers,
  flattenGroups,
  suggestNextChildNumber,
  suggestNextTopLevelNumber,
} from "../utils/offer-group-hierarchy";
import { getOfferAsync, updateOfferAsync } from "../../../../slices/offer";
import {
  getOfferCalculationAsync,
  updateOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { resolveOfferActionErrorMessage } from "../utils";

/**
 * Persist / add / edit Offer::Group hierarchy (Claude numbering 1.0 / 1.1).
 *
 * Owner is either:
 * - an Offer (offer-scoped chapters), or
 * - a firm calculation template (offer_id nil) via calculation-owned groups.
 */
export default function useOfferGroups(owner) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [groupDialog, setGroupDialog] = useState({
    open: false,
    mode: "create",
    group: null,
    parentGroup: null,
  });

  const resolvedKind =
    owner?.kind === "calculation" || owner?.kind === "offer"
      ? owner.kind
      : owner?.id &&
          Object.prototype.hasOwnProperty.call(owner, "offer_id") &&
          owner.offer_id == null
        ? "calculation"
        : owner?.id
          ? "offer"
          : null;

  const groups = useMemo(
    () => assignDisplayNumbers(flattenGroups(owner?.groups || [])),
    [owner?.groups]
  );

  const persistGroups = useCallback(
    async (nextGroups) => {
      if (!owner?.id || !resolvedKind) return false;
      setSaving(true);
      try {
        const payload = nextGroups.map((g, index) => ({
          id: g.id,
          title: g.title || "",
          comment: g.comment || g.description || "",
          row_order: g.row_order ?? index,
          parent_id: g.parent_id ?? null,
          number: g.number || null,
          discipline: g.discipline || null,
          group_type: g.group_type || null,
          ...(g.delete ? { delete: true } : {}),
        }));

        if (resolvedKind === "calculation") {
          const result = await dispatch(
            updateOfferCalculationAsync({
              id: owner.id,
              groups: payload,
            })
          );
          if (updateOfferCalculationAsync.rejected.match(result)) {
            toast.error(
              resolveOfferActionErrorMessage(
                t,
                result.payload,
                "offers.groups.save_failed"
              )
            );
            return false;
          }
          await dispatch(getOfferCalculationAsync(owner.id));
          toast.success(t("offers.groups.saved"));
          return true;
        }

        const result = await dispatch(
          updateOfferAsync({ id: owner.id, groups: payload })
        );
        if (updateOfferAsync.rejected.match(result)) {
          toast.error(
            resolveOfferActionErrorMessage(
              t,
              result.payload,
              "offers.groups.save_failed"
            )
          );
          return false;
        }
        await dispatch(getOfferAsync(owner.id));
        toast.success(t("offers.groups.saved"));
        return true;
      } finally {
        setSaving(false);
      }
    },
    [dispatch, owner?.id, resolvedKind, t]
  );

  const openCreateGroupDialog = useCallback((parentNode = null) => {
    setGroupDialog({
      open: true,
      mode: "create",
      group: null,
      parentGroup: parentNode || null,
    });
  }, []);

  const openEditGroupDialog = useCallback((node) => {
    if (!node?.id) return;
    setGroupDialog({
      open: true,
      mode: "edit",
      group: node,
      parentGroup: null,
    });
  }, []);

  const closeGroupDialog = useCallback(() => {
    setGroupDialog((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Create or update a structure node from dialog values, then persist.
   * @returns {Promise<boolean>}
   */
  const saveStructureGroup = useCallback(
    async (values) => {
      if (!values) return false;
      const trimmedTitle = (values.title || "").trim();
      if (!trimmedTitle) {
        toast.error(t("offers.groups.title_required"));
        return false;
      }

      const parentId =
        values.parent_id === "" || values.parent_id == null
          ? null
          : values.parent_id;
      const comment = (values.comment || values.description || "").trim();
      const discipline = (values.discipline || "").trim() || null;
      const groupType = (values.group_type || "").trim() || null;

      if (values.id != null) {
        const next = groups.map((g) =>
          String(g.id) === String(values.id)
            ? {
                ...g,
                title: trimmedTitle,
                comment,
                discipline,
                group_type: groupType,
                parent_id: parentId,
              }
            : { ...g }
        );
        return persistGroups(next);
      }

      const number = parentId
        ? suggestNextChildNumber(groups, parentId)
        : suggestNextTopLevelNumber(groups);
      const next = [
        ...groups.map((g) => ({ ...g })),
        {
          title: trimmedTitle,
          comment,
          row_order: groups.length,
          parent_id: parentId,
          number,
          discipline: discipline || trimmedTitle,
          group_type: groupType,
        },
      ];
      return persistGroups(next);
    },
    [groups, persistGroups, t]
  );

  /**
   * Delete a structure group and its descendants. Kalkyle lines keep their
   * membership cleared via API nullify (offer_group_id).
   */
  const deleteStructureGroup = useCallback(
    async (node) => {
      if (!node?.id) return null;
      const rootId = String(node.id);
      const byParent = new Map();
      groups.forEach((g) => {
        if (g?.id == null) return;
        const pid = g.parent_id == null ? null : String(g.parent_id);
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(g);
      });
      const toDelete = new Set([rootId]);
      const walk = (id) => {
        (byParent.get(String(id)) || []).forEach((child) => {
          const cid = String(child.id);
          if (toDelete.has(cid)) return;
          toDelete.add(cid);
          walk(cid);
        });
      };
      walk(rootId);

      const title =
        (node.title || node.discipline || "").trim() ||
        t("offers.groups.untitled");
      const confirmed = window.confirm(
        toDelete.size > 1
          ? t("offers.groups.delete_confirm_with_children", {
              title,
              count: toDelete.size - 1,
            })
          : t("offers.groups.delete_confirm", { title })
      );
      if (!confirmed) return null;

      const next = groups.map((g) =>
        toDelete.has(String(g.id)) ? { ...g, delete: true } : { ...g }
      );
      const ok = await persistGroups(next);
      if (!ok) return null;
      if (selectedGroupId != null && toDelete.has(String(selectedGroupId))) {
        setSelectedGroupId(null);
      }
      return toDelete;
    },
    [groups, persistGroups, selectedGroupId, t]
  );

  /** @deprecated Use openCreateGroupDialog — kept as alias for tree wiring */
  const handleAddGroup = openCreateGroupDialog;
  /** @deprecated Use openEditGroupDialog — kept as alias for tree wiring */
  const handleRenameGroup = openEditGroupDialog;

  return {
    groups,
    selectedGroupId,
    setSelectedGroupId,
    saving,
    persistGroups,
    groupDialog,
    openCreateGroupDialog,
    openEditGroupDialog,
    closeGroupDialog,
    saveStructureGroup,
    deleteStructureGroup,
    handleAddGroup,
    handleRenameGroup,
    ownerKind: resolvedKind,
  };
}
